import { getStyle, resolveText, uid, followAttachedArtwork } from "./studioModel";
import { textLines } from "./StudioCanvas";

export function detachContent(node, content) {
  const next = { ...node, text: resolveText(node, content) };
  for (const key of ["binding", "preserveOriginal", "originalText", "sourceSnapshot", "textOverride", "priceFormat"]) delete next[key];
  return next;
}

export function linkContent(node, binding, content) {
  const next = detachContent(node, content);
  delete next.originalLayout;
  return binding ? { ...next, binding, text: content[binding] || "" } : next;
}

// Remember observed descriptions so intentionally removed frames stay removed.
export function syncDescriptions(doc, content) {
  return placeNewDescriptions(reconcileCmsItems(doc, content), content);
}

// Add frames for descriptions that appeared in the CMS since they were last
// observed. An explicit repair: opening the studio never runs it.
export function placeNewDescriptions(doc, content) {
  // Print layouts with deliberate panel placement decide which descriptions fit.
  // Auto-inserting a new frame would shift text rows without moving section bars.
  if (doc.manualDescriptionLayout) return doc;
  const seen = { ...doc.cmsDescriptions };
  const pages = doc.pages.map((page) => {
    let elements = [...page.elements];
    for (const name of page.elements) {
      if (!/^item:[^:]+:name$/.test(name.binding || "")) continue;
      const binding = name.binding.replace(/:name$/, ":description");
      const key = `${page.id}:${name.id}`;
      const text = content[binding] || "";
      const previous = seen[key];
      seen[key] = text;
      if (!text.trim() || previous === text || elements.some((n) => n.binding === binding)) continue;
      const center = name.x + name.w / 2;
      const template = elements.filter((n) => n.role === "description" && Math.abs(n.x + n.w / 2 - center) < 30)
        .sort((a, b) => Math.abs(a.y - name.y) - Math.abs(b.y - name.y))[0];
      const style = template ? getStyle(doc, template) : { ...doc.styles.description, align: getStyle(doc, name).align };
      const w = template?.w || name.w;
      const x = style.align === "center" ? center - w / 2 : name.x;
      const h = Math.max(style.fontSize, textLines(text, style, w).length * style.fontSize * style.lineHeight) + 2;
      const y = name.y + name.h + 2;
      const next = { id: uid(), type: "text", role: "description", name: text.slice(0, 60), text, binding,
        x, y, w, h, style, opacity: 1, rotation: 0, groupId: name.groupId, groupPath: name.groupPath };
      // Make room in this column, retaining the other column and page artwork.
      const below = elements.filter((n) => n.type === "text" && n.y >= y - 1 && n.x < x + w && n.x + n.w > x);
      const firstY = Math.min(...below.map((n) => n.y));
      const shift = Math.max(0, y + h + 3 - firstY);
      if (shift) {
        const ids = new Set(below.map((n) => n.id));
        elements = elements.map((n) => ids.has(n.id) ? { ...n, y: n.y + shift } : n);
      }
      elements.push(next);
    }
    return { ...page, elements };
  });
  return followAttachedArtwork(doc, { ...doc, pages, cmsDescriptions: seen });
}

export function reconcileCmsItems(doc, content) {
  return { ...doc, pages: doc.pages.map((page) => {
    // Migrate only recognizable gluten-free symbols, never decorative flourishes.
    let elements = page.elements.map((n) => {
      if (n.type !== "image" || !/^gluten[ -]free$/i.test(n.name || "") || n.attachedTo) return n;
      const candidates = page.elements.filter((a) => /:name$/.test(a.binding || "") &&
        (n.itemId ? a.binding === `item:${n.itemId}:name` : Math.abs(a.y - n.y) < 18 && n.x <= a.x + a.w && n.x + n.w >= a.x - 28));
      const anchor = candidates.sort((a,b) => Math.abs(a.y-n.y)-Math.abs(b.y-n.y))[0];
      return anchor ? { ...n, attachedTo: anchor.id, itemId: anchor.binding.split(":")[1] } : n;
    });
    const loaded = page.sourceMenu ? content[`menu:${page.sourceMenu}:loaded`] : Object.keys(content).some((key) => key.startsWith("menu:"));
    if (loaded) {
      const removed = new Set(elements.filter((n) => /^item:[^:]+:/.test(n.binding || "") &&
        !Object.prototype.hasOwnProperty.call(content, n.binding.replace(/:[^:]+$/, ":name"))).map((n) => n.id));
      elements = elements.filter((n) => !removed.has(n.id) && !removed.has(n.attachedTo));
    }
    return { ...page, elements };
  }) };
}
